<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button text="Retour" :default-href="`/report-sheets/${sheet?.uuid}`"></ion-back-button>
                </ion-buttons>
                <ion-title>Rapport : {{ sheet?.title }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <div :class="$style['sub-header']">Travaux sous-traités ({{ sheet?.outsourced_works.length ?? 0 }})</div>

        <ion-content>
            <ContentSidebarWrapper side="left">
                <template v-slot:content>
                    <div v-if="!loaded">
                        <EquipmentSkeleton/>
                    </div>

                    <div v-else-if="sheet?.outsourced_works?.length" style="padding-bottom: 200px;">
                        <ion-accordion-group>
                            <ion-accordion v-for="outsourcedWork in sheet?.outsourced_works" :key="outsourcedWork.uuid" :value="outsourcedWork.uuid">
                                <ion-item slot="header" color="light">
                                    <ion-label>
                                        {{ outsourcedWork?.name ?? 'N/A' }}
                                        <span v-if="outsourcedWork.total_cost" :class="$style['total_cost']">Total : {{ outsourcedWork.total_cost ?? 0.00 }}€</span>
                                    </ion-label>
                                </ion-item>
                                
                                <div slot="content">
                                    <ion-item>
                                        <UnitySelector @ionChange="handleUnity(outsourcedWork, $event)" :disabled="fieldDisabled" v-model="outsourcedWork.unity" />
                                    </ion-item>

                                    <ion-item>
                                        <ion-input :disabled="fieldDisabled" label="Quantité :" placeholder="Enter data" v-model="outsourcedWork.quantity" @change="calculateTotalDailyCost(outsourcedWork)"></ion-input>
                                    </ion-item>

                                    <ion-item  v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="fieldDisabled" :label="outsourcedWork.unity === 'Hr' ? 'Coût horaire' : 'Coût unitaire'" v-model="outsourcedWork.hourly_cost" type="number" @change="calculateTotalDailyCost(outsourcedWork)"></ion-input>
                                    </ion-item>

                                    <ion-item v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="true" label="Coût total" v-model="outsourcedWork.total_cost"></ion-input>
                                    </ion-item>

                                    <div v-if="!fieldDisabled" :class="$style['container-button']">
                                        <ion-button v-if="false" slot="start" size="small" fill="outline" @click="presentActionOutsourcedWork(outsourcedWork)">Supprimer</ion-button>
                                        <ion-button slot="end" size="small" color="success" @click="formSubmit(outsourcedWork)">Enregistrer</ion-button>
                                    </div>
                                </div>
                            </ion-accordion>
                        </ion-accordion-group>
                    </div>

                    <div v-else>
                        <ion-text class="ion-text-center" color="medium">
                            <p>Aucun résultat</p>
                        </ion-text>
                    </div>
                </template>

                <template v-slot:sidebar v-if="loaded && sheet.total_outsourced_work_cost ">
                    <p :class="$style['total_day']">Coût total des trav. sous-traités : {{ sheet.total_outsourced_work_cost ?? 0.00 }}€</p>
                </template>
            </ContentSidebarWrapper>

            <template v-if="!sheet?.outsourced_work_step_validated || userCanForceEdit()">
                <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                    <ion-fab-button color="primary" @click="presentActionSheet">
                        <ion-icon :icon="caretUp"></ion-icon>
                    </ion-fab-button>
                </ion-fab>

                <OutsourcedWorkSelector :active="showOutsourcedWorkSelector" @close="showOutsourcedWorkSelector = false" @change="addOutsourcedWorkFromSelector($event.value)" filter="" />
            </template>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonHeader,
        IonPage,
        IonToolbar,
        IonItem,
        IonContent,
        toastController,
        IonFab,
        IonFabButton,
        actionSheetController,
        alertController,
        IonIcon,
        IonAccordionGroup,
        IonAccordion,
        IonInput
    } from "@ionic/vue"

    import {
        caretUp
    } from 'ionicons/icons'

    import { defineComponent } from "vue"
    import EquipmentSkeleton from "@/components/skeletons/EquipmentSkeleton.vue";
    import ContentSidebarWrapper from "@/components/wrappers/ContentSidebarWrapper.vue"
    import { ReportSheet } from "@/report-sheets"
    import { useUserStore } from "@/stores/user"
    import { OutsourcedWork } from "@/outsourced-works"
    import OutsourcedWorkSelector from "@/components/data/selectors/OutsourcedWorkSelector.vue"
    import UnitySelector from "@/components/forms/ui/UnitySelector.vue";

    export default defineComponent({
        components: {
            EquipmentSkeleton,
            ContentSidebarWrapper,
            OutsourcedWorkSelector,
            UnitySelector,
            IonBackButton,
            IonButtons,
            IonHeader,
            IonItem,
            IonPage,
            IonToolbar,
            IonContent,
            IonFab,
            IonFabButton,
            IonIcon,
            IonAccordionGroup,
            IonAccordion,
            IonInput
        },
        setup() {
            const user = useUserStore()

            return { 
                caretUp,
                user 
            }
        },
        data() {
            return {
                fieldDisabled: true,
                loaded: false as boolean | false,
                sheet: null as ReportSheet | null,
                showOutsourcedWorkSelector: false
            }
        },
        mounted() {
            if (this.user.is_logged_in)
                this.load()

            this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
                if (is_logged_in) this.load();
            });
        },
        methods: {
            load() {
                this.loaded = false

                this.axios.get(`/report-sheets/${this.$route.params.uuidReport}`)
                .then((responses) => {
                    this.sheet = responses.data

                    if (this.sheet && (!this.sheet.outsourced_work_step_validated || this.userCanForceEdit())) 
                        this.fieldDisabled = false
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            userCanForceEdit(): boolean {
                if (!this.sheet || !this.user) return false
                
                // Get user's role for this report sheet
                const userRole = this.sheet.roles?.find(role => role.user?.uuid === this.user.uuid)?.role
                
                if (!userRole) return false
                
                // Site-supervisor and manager can always modify, regardless of validation status
                if (['site-supervisor', 'manager'].includes(userRole)) {
                    return true
                }
                
                // Site-manager can modify if the report is not validated by site-manager or manager
                if (userRole === 'site-manager') {
                    return !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
                }
                
                // Other roles can only modify if no validation has been done
                return !this.sheet.validated_by_site_supervisor && !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
            },
            async presentActionOutsourcedWork(outsourcedWork: OutsourcedWork) {
                const alert = await alertController.create({
                    message: 'Etes-vous sûr de vouloir supprimer définitivement ?',
                    htmlAttributes: {
                        'aria-label': 'alert dialog'
                    },
                    buttons: [
                        {
                            text: 'Annuler',
                            role: "cancel"
                        },
                        {
                            text: 'Confirmer',
                            role: "confirm",
                            handler: () => {
                                this.deleteItem(outsourcedWork)
                            }
                        }
                    ]
                })

                await alert.present()
            },
            deleteItem(outsourcedWork: OutsourcedWork) {
                if (!this.loaded)
                    return

                this.loaded = false
                
                this.axios.delete(`/report-sheets/${this.$route.params.uuidReport}/outsourced-works/${outsourcedWork.uuid}`)
                .then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly deleted',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    if (this.sheet)
                        this.sheet.outsourced_works = this.sheet.outsourced_works.filter((o: OutsourcedWork) => o.uuid !== outsourcedWork.uuid)
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            handleUnity(outsourcedWork: OutsourcedWork, event: any) {
                outsourcedWork.quantity = event.detail.value === 'Hr' ? '8' : '1'

                this.calculateTotalDailyCost(outsourcedWork)
            },
            calculateTotalDailyCost(outsourcedWork: OutsourcedWork) {
                const hourlyCost = outsourcedWork.hourly_cost ? parseFloat(outsourcedWork.hourly_cost) : 0
                const quantity = outsourcedWork.quantity ? parseFloat(outsourcedWork.quantity) : 0

                const totalCost = quantity * hourlyCost
                outsourcedWork.total_cost = totalCost.toFixed(2)
            },
            formSubmit(outsourcedWork: OutsourcedWork) {
                if (!this.loaded)
                    return
                
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/outsourced-works/${outsourcedWork.uuid}`, {
                    name: outsourcedWork?.name,
                    unity: outsourcedWork?.unity,
                    quantity: outsourcedWork?.quantity,
                    hourly_cost: outsourcedWork?.hourly_cost
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly updated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.load()
                })
            },
            stepValidation() {
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/step-validation`, {
                    step: 'outsourced_work_step_validated'
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Outsourced work step validated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    this.$router.push(`/report-sheets/${this.$route.params.uuidReport}`)
                }).catch((error) => {
                    console.error(error)
                    this.loaded = true
                })
            },
            async presentActionSheet() {
                const actionSheet = await actionSheetController.create({
                    buttons: [
                        {
                            text: "Ajouter un travail sous-traité",
                            handler: () => {
                                this.triggerOutsourcedWorkSelector()
                            }
                        },
                        {
                            text: "Valider cette étape",
                            handler: async () => {
                                const alert = await alertController.create({
                                    message: 'Etes-vous sûr de vouloir valider cette étape ?',
                                    htmlAttributes: {
                                        'aria-label': 'alert dialog'
                                    },
                                    buttons: [
                                        {
                                            text: 'Annuler',
                                            role: "cancel"
                                        },
                                        {
                                            text: 'Confirmer',
                                            role: "confirm",
                                            handler: () => {
                                                this.stepValidation()
                                            }
                                        }
                                    ]
                                })

                                await alert.present()
                            }
                        },
                        {
                            text: "Annuler",
                            role: "cancel"
                        }
                    ]
                })

                await actionSheet.present()
            },
            triggerOutsourcedWorkSelector() {
                this.showOutsourcedWorkSelector = true
            },
            addOutsourcedWorkFromSelector(selectedOutsourcedWork: any) {
                console.log('selectedOutsourcedWork', selectedOutsourcedWork);
                
                if (!selectedOutsourcedWork) return;
                                
                // Create a new report sheet outsourced work based on the selected one
                this.axios.post(`/report-sheets/${this.$route.params.uuidReport}/outsourced-works`, {
                    name: selectedOutsourcedWork.name+' '+selectedOutsourcedWork.contractor_name+' '+'('+selectedOutsourcedWork.reference+')',
                    unity: 'Jo', // Default unity
                    quantity: '1', // Default quantity
                    hourly_cost: '0.00', // Default cost
                    work_id: selectedOutsourcedWork.uuid // Link to the master outsourced work
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Travail sous-traité ajouté',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()
                    
                    this.load(); // Reload the list
                }).catch((error) => {
                    console.error(error)
                });
            }
        }
    })
</script>

<style lang="scss" module>
    .sub-header {
        background: #000;
        color:white;
        height: 50px;
        width: 100%;
        display: grid;
        align-content: center;
        justify-content: center;
    }
    .inner {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        padding: 1em;
        font-size: .9em;

        @media(max-width: 990px) {
            font-size: .8em;
        }
        @media(max-width: 700px) {
            flex-direction: column
        }
        h4, p {
            padding: 0;
            margin: 0;
        }
        p {
            margin-top: .25em;
            color: var(--ion-color-medium);
        }
        .right {
            display: flex;
            align-items: center;

            @media(min-width: 701px) {
                flex-direction: column;
                align-items: flex-end;
            }

            @media(max-width: 700px) {
                .date {
                    &:before {
                        content: '—';
                        margin: 0 .5em;
                        margin-top: 0;
                    }
                }
            }
        }
    }
    .red {
        color: var(--ion-color-primary)
    }
    ion-item {
        --inner-padding-end: 0;
        --padding-end: 16px;
    }
    ion-select {
        justify-content: flex-start;
    }
    .container-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 12px;
    }
    .total_cost {
        display: block;
        margin-top: .25em;
        color: var(--ion-color-medium);
    }
    .total_day {
        color: #fff;
        background: #9e9e9e;
        margin: 0;
        padding: 10px;
        text-align: center;
    }
</style>